/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";
import { ConfirmationNumber } from "@mui/icons-material";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
// Data
import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";
import { useState, useEffect } from "react";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import ArgonInput from "components/ArgonInput";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import PaginationBar from "components/PaginationBar/PaginationBar";
import axios from "utils/api";
import { CircularProgress } from "@mui/material";

function Tables() {
  //popup
  const [deleteDialog, setDeleteDialog] = useState(false);

  // Delete button click handler
  const handleDeleteClick = () => {
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
  };

  // State management
  const [classData, setClassData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [counts, setCounts] = useState({
    all: 0,
    active: 0,
    hold: 0,
    completed: 0,
    discontinued: 0,
    sme: 0,
    combined: 0,
    separate: 0,
  });

  // Fetch classes from API
  useEffect(() => {
    fetchNormalClasses();
  }, []);

  const fetchNormalClasses = async () => {
    try {
      setLoading(true);
      // Fetch normal classes using the new endpoint
      const response = await axios.get("/classes/sessions/all", {
        params: {
          page: 1,
          limit: 100,
        },
      });

      if (response.data.success) {
        const sessions = response.data.data || [];
        setClassData(sessions);

        // Calculate counts based on status and class type
        const newCounts = {
          all: sessions.length,
          active: sessions.filter(
            (s) => s.status === "ONGOING" || s.status === "CREATED" || s.status === "SCHEDULED"
          ).length,
          hold: sessions.filter((s) => s.status === "ON_HOLD").length,
          completed: sessions.filter((s) => s.status === "COMPLETED").length,
          discontinued: sessions.filter((s) => s.status === "DISCONTINUED").length,
          sme: sessions.filter((s) => s.classType === "SME").length,
          // Combined: Multiple students in one class (COMBINED classType OR more than 1 student)
          combined: sessions.filter(
            (s) =>
              s.classType === "COMBINED" ||
              (s.students && Array.isArray(s.students) && s.students.length > 1)
          ).length,
          // Separate: Single student per class (exactly 1 student)
          separate: sessions.filter(
            (s) => s.students && Array.isArray(s.students) && s.students.length === 1
          ).length,
        };
        setCounts(newCounts);
      }
    } catch (error) {
      console.error("Error fetching normal classes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on active tab
  const getFilteredData = () => {
    let filtered = classData;

    // Filter by tab
    switch (activeTab) {
      case "Active":
        filtered = filtered.filter(
          (s) => s.status === "ONGOING" || s.status === "CREATED" || s.status === "SCHEDULED"
        );
        break;
      case "Hold":
        filtered = filtered.filter((s) => s.status === "ON_HOLD");
        break;
      case "Completed":
        filtered = filtered.filter((s) => s.status === "COMPLETED");
        break;
      case "Discontinued":
        filtered = filtered.filter((s) => s.status === "DISCONTINUED");
        break;
      case "SME":
        filtered = filtered.filter((s) => s.classType === "SME");
        break;
      case "Combined":
        // Combined: Multiple students in one class
        filtered = filtered.filter(
          (s) =>
            s.classType === "COMBINED" ||
            (s.students && Array.isArray(s.students) && s.students.length > 1)
        );
        break;
      case "Separate":
        // Separate: Single student per class (exactly 1 student)
        filtered = filtered.filter(
          (s) => s.students && Array.isArray(s.students) && s.students.length === 1
        );
        break;
      default:
        break;
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter((item) => {
        const studentName = item.student?.name?.toLowerCase() || "";
        const tutorName = item.tutor?.name?.toLowerCase() || "";
        const parentName =
          item.parentId?.fullName?.toLowerCase() || item.parentId?.name?.toLowerCase() || "";
        return (
          studentName.includes(searchQuery.toLowerCase()) ||
          tutorName.includes(searchQuery.toLowerCase()) ||
          parentName.includes(searchQuery.toLowerCase())
        );
      });
    }

    return filtered;
  };

  // Get filtered data
  const filteredData = getFilteredData();

  // Pagination
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const { columns, rows } = ClassMgtTableData(handleDeleteClick, paginatedData);

  const tabsData = [
    { label: "All", count: counts.all, color: "#2027e1ff", content: "" },
    { label: "Active", count: counts.active, color: "#ec3c10ff", content: "" },
    { label: "Hold", count: counts.hold, color: "#ec10c7ff", content: "" },
    { label: "Completed", count: counts.completed, color: "#10a6ecff", content: "" },
    { label: "Discontinued", count: counts.discontinued, color: "#1a6808b7", content: "" },
    { label: "SME", count: counts.sme, color: "#1a6808b7", content: "" },
    { label: "Combined", count: counts.combined, color: "#1a6808b7", content: "" },
    { label: "Separate", count: counts.separate, color: "#1a6808b7", content: "" },
  ];

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1); // Reset to first page when changing tab
  };

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Search handler
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                All Classes
              </ArgonTypography>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between">
                <ArgonBox sx={{ height: "36px" }}>
                  <ApnaTabs tabsData={tabsData} onTabChange={handleTabChange} />
                </ArgonBox>
                <ArgonBox
                  sx={{ height: "36px" }}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <ArgonInput
                    placeholder="search"
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    sx={{ margin: "10px" }}
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={5}>
                  <CircularProgress color="primary" />
                </ArgonBox>
              ) : filteredData.length === 0 ? (
                <ArgonBox textAlign="center" py={8}>
                  <ConfirmationNumber sx={{ fontSize: 80, color: "#cbd5e1", mb: 2 }} />
                  <ArgonTypography variant="h5" color="#64748b" fontWeight="bold" mb={1}>
                    No Classes Found
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#94a3b8">
                    Try adjusting the filters or check back later.
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {!loading && filteredData.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={filteredData.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>

        {/* dialog popup */}

        <DeleteDialog
          open={deleteDialog}
          onClose={handleCloseDialog}
          onConfirm={handleCloseDialog}
          title="Delete Class"
          description="Are you sure you want to delete this class?"
          confirmText="Delete Class"
          cancelText="Cancel"
        />
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
